import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Activity, Play, Square, RefreshCw, ChevronUp, ChevronDown,
  ChevronsUpDown, MapPin, Thermometer, Droplets, Mountain, Zap,
  FlaskConical, CheckCircle2, Clock,
} from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";

interface WorkerStats {
  worker: {
    phase: "idle" | "downloading" | "processing" | "completed" | "stopped" | "error";
    status?: string;
    processed: number;
    total: number;
    eta: string | null;
    rate: number;
    isRunning: boolean;
    startedAt?: string;
    tilesTotal: number;
    tilesLoaded: number;
    tilesSkipped: number;
    currentDownload: string | null;
    downloadBytesTotal: number;
    downloadBytesReceived: number;
  };
  database: {
    pending: number;
    completed: number;
    errors: number;
  };
}

interface ImovelData {
  id: number;
  cod_imovel: string;
  uf: string;
  municipio: string;
  num_area: string;
  alt_media: number | null;
  alt_min: number | null;
  alt_max: number | null;
  centroid_lat: number | null;
  centroid_lon: number | null;
  perimetro_km: number | null;
  declividade: number | null;
  temp_media: number | null;
  precip_anual: number | null;
}

interface UfStats {
  uf: string;
  total: string;
  alt_media_avg: string;
  alt_min_global: string;
  alt_max_global: string;
  decliv_avg: string;
  temp_avg: string;
  precip_avg: string;
  perim_avg: string;
}

interface DataResponse {
  data: ImovelData[];
  total: number;
  limit: number;
  offset: number;
  byUf: UfStats[];
}

type SortField =
  | "alt_media" | "alt_min" | "alt_max" | "num_area"
  | "municipio" | "cod_imovel" | "declividade" | "temp_media"
  | "precip_anual" | "perimetro_km";

interface OghWorkerStats {
  worker: {
    phase: "idle" | "downloading" | "processing" | "completed" | "stopped" | "error";
    processed: number;
    total: number;
    errors: number;
    unavailable: number;
    eta: string | null;
    rate: number;
    isRunning: boolean;
    startedAt?: string;
    downloadProgress: {
      filesTotal: number;
      filesCompleted: number;
      currentFile: string;
      bytesDownloaded: number;
      bytesTotal: number;
    };
  };
  database: {
    completed: number | string;
    pending: number | string;
    totalWithCoords?: number | string;
    avg_0_30: string | null;
    avg_30_60: string | null;
    avg_60_100: string | null;
    avg_media: string | null;
    byUf: { uf: string; completed: string }[];
  };
}

interface OghImovelData {
  id: number;
  cod_imovel: string;
  uf: string;
  municipio: string;
  num_area: string;
  argila_0_30: number | null;
  argila_30_60: number | null;
  argila_60_100: number | null;
  argila_media: number | null;
  centroid_lat: number | null;
  centroid_lon: number | null;
}

interface OghUfStats {
  uf: string;
  total: string;
  argila_avg: string;
}

interface OghDataResponse {
  data: OghImovelData[];
  total: number;
  byUf: OghUfStats[];
}

type OghSortField = "argila_0_30" | "argila_30_60" | "argila_60_100" | "argila_media" | "cod_imovel" | "municipio" | "num_area";

const STATUS_LABEL: Record<string, string> = {
  idle: "Ocioso", running: "Executando", stopped: "Parado",
  completed: "Concluído", error: "Erro", paused: "Pausado",
  downloading: "Baixando Tiles", processing: "Processando",
};

const STATUS_COLOR: Record<string, string> = {
  idle: "bg-muted text-muted-foreground border-transparent",
  running: "bg-green-500/20 text-green-400 border-green-500/30",
  downloading: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  processing: "bg-green-500/20 text-green-400 border-green-500/30",
  stopped: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  error: "bg-destructive/20 text-destructive border-destructive/30",
};

function fmt(val: number | string | null | undefined, decimals = 1): string {
  if (val == null || val === "") return "—";
  const n = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(n)) return "—";
  return n.toFixed(decimals);
}

function SortIcon({ field, sort, order }: { field: SortField; sort: SortField; order: "asc" | "desc" }) {
  if (sort !== field) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 opacity-40" />;
  return order === "asc"
    ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-primary" />
    : <ChevronDown className="inline h-3 w-3 ml-0.5 text-primary" />;
}

function OghSortIcon({ field, sort, order }: { field: OghSortField; sort: OghSortField; order: "asc" | "desc" }) {
  if (sort !== field) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 opacity-40" />;
  return order === "asc"
    ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-amber-400" />
    : <ChevronDown className="inline h-3 w-3 ml-0.5 text-amber-400" />;
}

function AnimatedNumber({ value, className }: { value: number; className?: string }) {
  const [displayed, setDisplayed] = useState(value);
  const prevRef = useRef(value);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    if (from === to) return;
    prevRef.current = to;
    const diff = to - from;
    const steps = 20;
    const stepSize = diff / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      setDisplayed(Math.round(from + stepSize * step));
      if (step >= steps) { setDisplayed(to); clearInterval(timer); }
    }, 25);
    return () => clearInterval(timer);
  }, [value]);

  return <span className={className}>{displayed.toLocaleString("pt-BR")}</span>;
}

export default function SicarWorkerPage() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [autoRefresh, setAutoRefresh] = useState(true);

  // ── Altitude: table state ──────────────────────────────────────────────────
  const [sort, setSort] = useState<SortField>("alt_media");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [ufFilter, setUfFilter] = useState<string>("_all");
  const [altMin, setAltMin] = useState("");
  const [altMax, setAltMax] = useState("");
  const [selectedUf, setSelectedUf] = useState<string | null>(null);
  const limit = 50;

  // Animated counters for altitude
  const [liveCompleted, setLiveCompleted] = useState(0);
  const [livePending, setLivePending] = useState(0);
  const liveRef = useRef({ completed: 0, pending: 0, rate: 0, running: false });

  // ── OGH Argila: table state ────────────────────────────────────────────────
  const [oghSort, setOghSort] = useState<OghSortField>("argila_media");
  const [oghOrder, setOghOrder] = useState<"asc" | "desc">("desc");
  const [oghPage, setOghPage] = useState(0);
  const [oghUfFilter, setOghUfFilter] = useState<string>("_all");
  const oghLimit = 50;

  // ── Altitude worker query ──────────────────────────────────────────────────
  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery<WorkerStats>({
    queryKey: ["/api/vps/altitude/status"],
    queryFn: () => authFetch("/api/vps/altitude/status"),
    refetchInterval: autoRefresh ? 10000 : false,
  });

  useEffect(() => {
    if (!status) return;
    const completed = status.database.completed;
    const pending = status.database.pending;
    const rate = status.worker.rate;
    const running = status.worker.isRunning ?? false;
    liveRef.current = { completed, pending, rate, running };
    setLiveCompleted(completed);
    setLivePending(pending);
  }, [status]);

  useEffect(() => {
    const tick = setInterval(() => {
      const { rate, running } = liveRef.current;
      if (!running || rate <= 0) return;
      const perSecond = rate / 60;
      setLiveCompleted((prev) => { const next = Math.round(prev + perSecond); liveRef.current.completed = next; return next; });
      setLivePending((prev) => { const next = Math.max(0, Math.round(prev - perSecond)); liveRef.current.pending = next; return next; });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const buildDataParams = useCallback(() => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(page * limit), sort, order });
    const activeUf = selectedUf || (ufFilter !== "_all" ? ufFilter : "");
    if (activeUf) params.set("uf", activeUf);
    if (altMin) params.set("alt_min", altMin);
    if (altMax) params.set("alt_max", altMax);
    return params.toString();
  }, [page, sort, order, ufFilter, altMin, altMax, selectedUf]);

  const { data: dataResp, isLoading: dataLoading, refetch: refetchData } = useQuery<DataResponse>({
    queryKey: ["/api/sicar/altitude/data", page, sort, order, ufFilter, altMin, altMax, selectedUf],
    queryFn: () => authFetch(`/api/sicar/altitude/data?${buildDataParams()}`),
    refetchInterval: autoRefresh ? 5000 : false,
  });

  const startMut = useMutation({
    mutationFn: () => authFetch("/api/sicar/altitude/start", { method: "POST" }),
    onSuccess: () => { toast({ title: "Worker iniciado" }); qc.invalidateQueries({ queryKey: ["/api/vps/altitude/status"] }); },
    onError: (e: any) => toast({ title: "Erro ao iniciar", description: e.message, variant: "destructive" }),
  });

  const stopMut = useMutation({
    mutationFn: () => authFetch("/api/sicar/altitude/stop", { method: "POST" }),
    onSuccess: () => { toast({ title: "Worker parado" }); qc.invalidateQueries({ queryKey: ["/api/vps/altitude/status"] }); },
    onError: (e: any) => toast({ title: "Erro ao parar", description: e.message, variant: "destructive" }),
  });

  // ── OGH Argila worker query ────────────────────────────────────────────────
  const { data: oghStatus, isLoading: oghStatusLoading, refetch: refetchOghStatus } = useQuery<OghWorkerStats>({
    queryKey: ["/api/vps/opengeohub/status"],
    queryFn: () => authFetch("/api/vps/opengeohub/status"),
    refetchInterval: autoRefresh ? 10000 : false,
  });

  const oghStartMut = useMutation({
    mutationFn: () => authFetch("/api/sicar/opengeohub/start", { method: "POST" }),
    onSuccess: (r: any) => { toast({ title: r?.message ?? "Argila Worker iniciado" }); qc.invalidateQueries({ queryKey: ["/api/vps/opengeohub/status"] }); },
    onError: (e: any) => toast({ title: "Erro ao iniciar", description: e.message, variant: "destructive" }),
  });

  const oghStopMut = useMutation({
    mutationFn: () => authFetch("/api/sicar/opengeohub/stop", { method: "POST" }),
    onSuccess: () => { toast({ title: "Argila Worker parado" }); qc.invalidateQueries({ queryKey: ["/api/vps/opengeohub/status"] }); },
    onError: (e: any) => toast({ title: "Erro ao parar", description: e.message, variant: "destructive" }),
  });

  const buildOghParams = useCallback(() => {
    const params = new URLSearchParams({ limit: String(oghLimit), offset: String(oghPage * oghLimit), sort: oghSort, order: oghOrder });
    if (oghUfFilter !== "_all") params.set("uf", oghUfFilter);
    return params.toString();
  }, [oghPage, oghSort, oghOrder, oghUfFilter]);

  const { data: oghDataResp, isLoading: oghDataLoading, refetch: refetchOghData } = useQuery<OghDataResponse>({
    queryKey: ["/api/sicar/opengeohub/data", oghPage, oghSort, oghOrder, oghUfFilter],
    queryFn: () => authFetch(`/api/sicar/opengeohub/data?${buildOghParams()}`),
    refetchInterval: autoRefresh ? 10000 : false,
  });

  // ── Derived values ─────────────────────────────────────────────────────────
  const workerPhase = status?.worker?.phase ?? "idle";
  const workerStatus = workerPhase;
  const isRunning = status?.worker?.isRunning ?? false;
  const errors = status?.database?.errors ?? 0;
  const liveTotal = liveCompleted + livePending;
  const liveProgress = liveTotal > 0 ? (liveCompleted / liveTotal) * 100 : 0;
  const rate = status?.worker?.rate ?? 0;
  const eta = status?.worker?.eta ?? null;
  const rows = dataResp?.data ?? [];
  const totalRows = dataResp?.total ?? 0;
  const pageCount = Math.ceil(totalRows / limit);
  const byUf = dataResp?.byUf ?? [];

  const oghPhase = oghStatus?.worker?.phase ?? "idle";
  const oghIsRunning = oghPhase === "downloading" || oghPhase === "processing";
  const oghCompleted = Number(oghStatus?.database?.completed ?? 0);
  const oghPending   = Number(oghStatus?.database?.pending   ?? 0);
  const oghTotal     = oghCompleted + oghPending;
  const oghProgress  = oghTotal > 0 ? (oghCompleted / oghTotal) * 100 : 0;
  const oghRows = oghDataResp?.data ?? [];
  const oghTotalRows = oghDataResp?.total ?? 0;
  const oghPageCount = Math.ceil(oghTotalRows / oghLimit);
  const oghByUf = oghDataResp?.byUf ?? [];

  const OGH_PHASE_LABEL: Record<string, string> = {
    idle: "Ocioso", downloading: "Baixando COGs", processing: "Processando",
    completed: "Concluído", stopped: "Parado", error: "Erro",
  };

  function handleRefresh() { refetchStatus(); refetchData(); refetchOghStatus(); refetchOghData(); }
  function handleSort(field: SortField) {
    if (sort === field) setOrder((o) => o === "desc" ? "asc" : "desc");
    else { setSort(field); setOrder("desc"); }
    setPage(0);
  }
  function handleOghSort(field: OghSortField) {
    if (oghSort === field) setOghOrder((o) => o === "desc" ? "asc" : "desc");
    else { setOghSort(field); setOghOrder("desc"); }
    setOghPage(0);
  }
  function handleUfCardClick(uf: string) { setSelectedUf((prev) => prev === uf ? null : uf); setPage(0); }

  return (
    <div className="space-y-6" data-testid="page-sicar-worker">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-sicar-worker-title">SICAR Worker</h1>
            <p className="text-sm text-muted-foreground">Enriquecimento geoespacial — altitude/clima e argila OpenGeoHub</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors ${autoRefresh ? "bg-primary/10 border-primary text-primary" : "border-muted hover:bg-muted text-muted-foreground"}`}
            data-testid="button-auto-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${autoRefresh ? "animate-spin" : ""}`} />
            {autoRefresh ? "Ao vivo ON" : "Ao vivo OFF"}
          </button>
          <Button size="sm" variant="outline" onClick={handleRefresh} data-testid="button-refresh-status">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* ── Worker cards side by side ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* ── Altitude Worker card ── */}
        <Card data-testid="card-worker-status" className={isRunning ? "border-green-500/30" : ""}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Mountain className="h-4 w-4 text-primary" />
              Altitude Worker
              {statusLoading ? (
                <Skeleton className="h-5 w-20 ml-auto" />
              ) : (
                <span className={`ml-auto text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_COLOR[workerStatus]}`} data-testid="badge-worker-status">
                  {isRunning && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse mr-1.5" />}
                  {STATUS_LABEL[workerStatus] ?? workerStatus}
                </span>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">Altitude · Centroide · Declividade · Temperatura · Precipitação</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {statusLoading ? (
              <><Skeleton className="h-3 w-full" /><Skeleton className="h-16 w-full" /></>
            ) : (
              <>
                {/* Fase 1 — Download de tiles SRTM */}
                {(workerPhase === "downloading" || (status?.worker?.tilesLoaded ?? 0) > 0) && (
                  <div className={`rounded-md border p-2.5 space-y-1.5 ${workerPhase === "downloading" ? "bg-blue-500/5 border-blue-500/20" : "bg-muted/30 border-transparent"}`}>
                    <div className="flex items-center gap-1.5 text-xs font-medium">
                      {workerPhase === "downloading"
                        ? <><span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />Baixando tiles SRTM 90m (CGIAR)</>
                        : <><CheckCircle2 className="h-3 w-3 text-green-400" />Tiles SRTM carregados</>
                      }
                      <span className="ml-auto text-muted-foreground tabular-nums">
                        {status?.worker?.tilesLoaded ?? 0}/{(status?.worker?.tilesTotal ?? 81) - (status?.worker?.tilesSkipped ?? 0)} tiles
                      </span>
                    </div>
                    {workerPhase === "downloading" && (status?.worker?.downloadBytesTotal ?? 0) > 0 && (() => {
                      const recv = status?.worker?.downloadBytesReceived ?? 0;
                      const total = status?.worker?.downloadBytesTotal ?? 1;
                      const filePct = Math.min(100, (recv / total) * 100);
                      const tileName = status?.worker?.currentDownload ?? "";
                      return (
                        <>
                          <Progress value={filePct} className="h-2 bg-muted" indicatorClassName="bg-gradient-to-r from-blue-600 to-blue-400" data-testid="progress-srtm-download" />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span className="font-mono truncate">{tileName}.zip</span>
                            <span className="tabular-nums">{(recv / 1048576).toFixed(1)} / {(total / 1048576).toFixed(1)} MB</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Fase 2 — Processamento */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground flex items-center gap-1">
                      {workerPhase === "processing"
                        ? <><Zap className="h-2.5 w-2.5 text-yellow-400 animate-pulse" />Processando imóveis</>
                        : workerPhase === "downloading"
                          ? <span className="text-muted-foreground/60">Processamento (aguardando tiles)</span>
                          : "Progresso"
                      }
                    </span>
                    <span className="font-medium tabular-nums" data-testid="text-progress-pct">{liveProgress.toFixed(3)}%</span>
                  </div>
                  <Progress
                    value={Math.max(liveTotal > 0 ? 0.15 : 0, liveProgress)}
                    className="h-3 bg-muted"
                    indicatorClassName={workerPhase === "processing" ? "bg-gradient-to-r from-green-600 to-emerald-400" : workerPhase === "downloading" ? "bg-muted-foreground/30" : "bg-primary"}
                    data-testid="progress-worker"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span className="tabular-nums">
                      <AnimatedNumber value={liveCompleted} /> / <AnimatedNumber value={liveTotal} />
                    </span>
                    {eta && <span>ETA: <span className="text-green-400 font-medium">{eta}</span></span>}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className={`rounded-md p-2 border text-center ${isRunning ? "bg-green-500/5 border-green-500/20" : "bg-muted/40 border-transparent"}`} data-testid="stat-completed">
                    <p className="text-xs text-muted-foreground">Processados</p>
                    <p className="text-base font-bold text-green-400 tabular-nums"><AnimatedNumber value={liveCompleted} /></p>
                  </div>
                  <div className={`rounded-md p-2 border text-center ${isRunning ? "bg-yellow-500/5 border-yellow-500/20" : "bg-muted/40 border-transparent"}`} data-testid="stat-pending">
                    <p className="text-xs text-muted-foreground">Pendentes</p>
                    <p className="text-base font-bold text-yellow-400 tabular-nums"><AnimatedNumber value={livePending} /></p>
                  </div>
                  <div className="bg-muted/40 rounded-md p-2 text-center" data-testid="stat-eta">
                    <p className="text-xs text-muted-foreground">Taxa</p>
                    <p className="text-base font-bold tabular-nums">
                      {rate > 0 ? <><span className="text-green-400">{rate.toLocaleString("pt-BR")}</span><span className="text-xs text-muted-foreground">/min</span></> : "—"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground px-0.5">
                  <span>Erros: <span className="text-destructive font-medium">{errors.toLocaleString("pt-BR")}</span></span>
                </div>

                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white flex-1"
                    onClick={() => startMut.mutate()} disabled={startMut.isPending || isRunning} data-testid="button-start-worker">
                    <Play className="h-3 w-3 mr-1" /> Iniciar
                  </Button>
                  <Button size="sm" variant="destructive" className="flex-1"
                    onClick={() => stopMut.mutate()} disabled={stopMut.isPending || !isRunning} data-testid="button-stop-worker">
                    <Square className="h-3 w-3 mr-1" /> Parar
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* ── OGH Argila Worker card ── */}
        <Card data-testid="card-ogh-worker" className={oghIsRunning ? "border-amber-500/30" : ""}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Droplets className="h-4 w-4 text-amber-500" />
              Argila Worker (OpenGeoHub)
              {oghStatusLoading ? (
                <Skeleton className="h-5 w-20 ml-auto" />
              ) : (
                <span className={`ml-auto text-xs px-2 py-0.5 rounded-full border font-medium ${
                  oghPhase === "processing" || oghPhase === "downloading"
                    ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                    : STATUS_COLOR[oghPhase] ?? "bg-muted text-muted-foreground border-transparent"
                }`} data-testid="badge-ogh-status">
                  {oghIsRunning && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse mr-1.5" />}
                  {OGH_PHASE_LABEL[oghPhase] ?? oghPhase}
                </span>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">Argila 0–30cm · 30–60cm · 60–100cm · Média — ISO 11277, 120m resolução, COG local</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {oghStatusLoading ? (
              <><Skeleton className="h-3 w-full" /><Skeleton className="h-16 w-full" /></>
            ) : (
              <>
                {/* Download step */}
                {(() => {
                  const dp = oghStatus?.worker?.downloadProgress;
                  const filesCompleted = dp?.filesCompleted ?? 0;
                  const filesTotal = dp?.filesTotal ?? 3;
                  const gbDown = (dp?.bytesDownloaded ?? 0) / 1e9;
                  const gbTotal = (dp?.bytesTotal ?? 0) / 1e9;
                  const filePct = gbTotal > 0 ? Math.min(100, (gbDown / gbTotal) * 100) : 0;
                  const downloadDone = oghPhase !== "downloading" && oghPhase !== "idle";
                  const downloadActive = oghPhase === "downloading";
                  return (
                    <div className="space-y-2">
                      {/* Step 1 — Download */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className={`flex items-center gap-1.5 font-medium ${downloadDone ? "text-green-400" : downloadActive ? "text-amber-400" : "text-muted-foreground"}`}>
                            {downloadDone ? <CheckCircle2 className="h-3 w-3" /> : downloadActive ? <Zap className="h-3 w-3 animate-pulse" /> : <Clock className="h-3 w-3" />}
                            Etapa 1 — Download COGs
                          </span>
                          <span className="text-muted-foreground tabular-nums">{downloadDone ? filesTotal : filesCompleted}/{filesTotal} arquivos</span>
                        </div>
                        <div className="flex gap-1" data-testid="dots-ogh-files">
                          {Array.from({ length: filesTotal }).map((_, i) => (
                            <div key={i} className={`flex-1 h-2 rounded-full transition-all duration-500 ${downloadDone || i < filesCompleted ? "bg-green-500" : i === filesCompleted && downloadActive ? "bg-amber-400/70" : "bg-muted"}`} />
                          ))}
                        </div>
                        {downloadActive && (
                          <>
                            <Progress value={filePct} className="h-2.5 bg-muted" indicatorClassName="bg-gradient-to-r from-amber-600 to-yellow-400" data-testid="progress-ogh-download" />
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span className="truncate max-w-[58%] opacity-70">{dp?.currentFile?.replace(/\.tif$/, "").slice(-30) ?? "..."}</span>
                              <span className="tabular-nums font-medium text-amber-400">{gbDown.toFixed(2)} / {gbTotal > 0 ? gbTotal.toFixed(1) : "~4.9"} GB</span>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="border-t border-border/40" />

                      {/* Step 2 — Processing + main progress */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className={`flex items-center gap-1.5 font-medium ${oghPhase === "completed" ? "text-green-400" : oghPhase === "processing" ? "text-amber-400" : "text-muted-foreground"}`}>
                            {oghPhase === "completed" ? <CheckCircle2 className="h-3 w-3" /> : oghPhase === "processing" ? <Zap className="h-3 w-3 animate-pulse" /> : <Clock className="h-3 w-3" />}
                            Etapa 2 — Processar registros
                          </span>
                          <span className="font-bold tabular-nums" data-testid="text-ogh-progress-pct">
                            {oghProgress.toFixed(oghProgress < 1 ? 3 : 2)}%
                          </span>
                        </div>
                        <Progress
                          value={Math.max(oghTotal > 0 && oghPhase === "processing" ? 0.12 : 0, oghProgress)}
                          className="h-3 bg-muted"
                          indicatorClassName={oghPhase === "processing" ? "bg-gradient-to-r from-amber-600 to-yellow-400" : oghPhase === "completed" ? "bg-green-500" : "bg-muted-foreground/20"}
                          data-testid="progress-ogh-worker"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span className="tabular-nums">{oghCompleted.toLocaleString("pt-BR")} / {oghTotal.toLocaleString("pt-BR")} registros</span>
                          {oghStatus?.worker?.eta && oghPhase === "processing" && (
                            <span>ETA: <span className="text-amber-400 font-medium">{oghStatus.worker.eta}</span></span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className={`rounded-md p-2 border text-center ${oghIsRunning ? "bg-amber-500/5 border-amber-500/20" : "bg-muted/40 border-transparent"}`} data-testid="stat-ogh-completed">
                    <p className="text-xs text-muted-foreground">Processados</p>
                    <p className="text-base font-bold text-amber-400 tabular-nums"><AnimatedNumber value={oghCompleted} /></p>
                  </div>
                  <div className={`rounded-md p-2 border text-center ${oghIsRunning ? "bg-yellow-500/5 border-yellow-500/20" : "bg-muted/40 border-transparent"}`} data-testid="stat-ogh-pending">
                    <p className="text-xs text-muted-foreground">Pendentes</p>
                    <p className="text-base font-bold text-yellow-400 tabular-nums"><AnimatedNumber value={oghPending} /></p>
                  </div>
                  <div className="bg-muted/40 rounded-md p-2 text-center" data-testid="stat-ogh-rate">
                    <p className="text-xs text-muted-foreground">Taxa</p>
                    <p className="text-base font-bold tabular-nums">
                      {oghStatus?.worker?.rate ? <><span className="text-amber-400">{oghStatus.worker.rate.toLocaleString("pt-BR")}</span><span className="text-xs text-muted-foreground">/min</span></> : "—"}
                    </p>
                  </div>
                  <div className="bg-muted/40 rounded-md p-2 text-center" data-testid="stat-ogh-eta">
                    <p className="text-xs text-muted-foreground">ETA</p>
                    <p className="text-sm font-bold tabular-nums">{oghStatus?.worker?.eta ?? "—"}</p>
                  </div>
                </div>

                {oghCompleted > 0 && (
                  <div className="grid grid-cols-4 gap-2 pt-1 border-t border-border/50">
                    {[
                      { label: "0–30cm", value: oghStatus?.database?.avg_0_30 },
                      { label: "30–60cm", value: oghStatus?.database?.avg_30_60 },
                      { label: "60–100cm", value: oghStatus?.database?.avg_60_100 },
                      { label: "Média %", value: oghStatus?.database?.avg_media },
                    ].map(({ label, value }) => (
                      <div key={label} className="text-center">
                        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                        <p className="text-sm font-semibold tabular-nums text-amber-400">{fmt(value)}%</p>
                      </div>
                    ))}
                  </div>
                )}

                {(oghStatus?.database?.byUf?.length ?? 0) > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {oghStatus!.database.byUf.slice(0, 27).map(({ uf, completed }) => (
                      <span key={uf} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-500/10 border border-amber-500/20 text-amber-300" data-testid={`badge-ogh-uf-${uf}`}>
                        {uf} <span className="opacity-70">{Number(completed).toLocaleString("pt-BR")}</span>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white flex-1"
                    onClick={() => oghStartMut.mutate()} disabled={oghStartMut.isPending || oghIsRunning} data-testid="button-ogh-start">
                    <Play className="h-3 w-3 mr-1" /> Iniciar
                  </Button>
                  <Button size="sm" variant="destructive" className="flex-1"
                    onClick={() => oghStopMut.mutate()} disabled={oghStopMut.isPending || !oghIsRunning} data-testid="button-ogh-stop">
                    <Square className="h-3 w-3 mr-1" /> Parar
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Data tabs ── */}
      <Tabs defaultValue="argila" className="space-y-4">
        <TabsList data-testid="tabs-sicar-worker">
          <TabsTrigger value="argila" data-testid="tab-argila">
            <Droplets className="h-3.5 w-3.5 mr-1.5 text-amber-400" />
            Argila ({oghTotalRows.toLocaleString("pt-BR")})
          </TabsTrigger>
          <TabsTrigger value="altitude" data-testid="tab-altitude">
            <Mountain className="h-3.5 w-3.5 mr-1.5 text-green-400" />
            Altitude ({totalRows.toLocaleString("pt-BR")})
          </TabsTrigger>
        </TabsList>

        {/* ── Tab: Argila ── */}
        <TabsContent value="argila" className="space-y-4">
          {/* Filters */}
          <Card data-testid="card-ogh-filters">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1 min-w-[140px]">
                  <Label className="text-xs">UF</Label>
                  <Select value={oghUfFilter} onValueChange={(v) => { setOghUfFilter(v); setOghPage(0); }}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-ogh-uf">
                      <SelectValue placeholder="Todos os estados" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">Todos os estados</SelectItem>
                      {oghByUf.map((s) => (
                        <SelectItem key={s.uf} value={s.uf} data-testid={`option-ogh-uf-${s.uf}`}>
                          {s.uf} ({parseInt(s.total).toLocaleString("pt-BR")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {oghUfFilter !== "_all" && (
                  <Button variant="outline" size="sm" className="h-8"
                    onClick={() => { setOghUfFilter("_all"); setOghPage(0); }} data-testid="button-ogh-clear-filters">
                    Limpar filtro
                  </Button>
                )}
                <div className="ml-auto text-xs text-muted-foreground self-end pb-1" data-testid="text-ogh-total-rows">
                  {oghTotalRows.toLocaleString("pt-BR")} registros com argila
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="table-ogh-imoveis">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => handleOghSort("cod_imovel")} data-testid="th-ogh-cod">
                        Cod. Imóvel <OghSortIcon field="cod_imovel" sort={oghSort} order={oghOrder} />
                      </TableHead>
                      <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => handleOghSort("municipio")} data-testid="th-ogh-municipio">
                        Município <OghSortIcon field="municipio" sort={oghSort} order={oghOrder} />
                      </TableHead>
                      <TableHead className="text-center">UF</TableHead>
                      <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => handleOghSort("num_area")} data-testid="th-ogh-area">
                        Área (ha) <OghSortIcon field="num_area" sort={oghSort} order={oghOrder} />
                      </TableHead>
                      <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => handleOghSort("argila_0_30")} data-testid="th-ogh-0-30">
                        0–30cm % <OghSortIcon field="argila_0_30" sort={oghSort} order={oghOrder} />
                      </TableHead>
                      <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => handleOghSort("argila_30_60")} data-testid="th-ogh-30-60">
                        30–60cm % <OghSortIcon field="argila_30_60" sort={oghSort} order={oghOrder} />
                      </TableHead>
                      <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => handleOghSort("argila_60_100")} data-testid="th-ogh-60-100">
                        60–100cm % <OghSortIcon field="argila_60_100" sort={oghSort} order={oghOrder} />
                      </TableHead>
                      <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => handleOghSort("argila_media")} data-testid="th-ogh-media">
                        Média % <OghSortIcon field="argila_media" sort={oghSort} order={oghOrder} />
                      </TableHead>
                      <TableHead className="text-right whitespace-nowrap">Lat</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Lon</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {oghDataLoading ? (
                      Array.from({ length: 10 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 10 }).map((_, j) => (
                            <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : oghRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-muted-foreground py-12">
                          {oghCompleted === 0
                            ? "Nenhum registro de argila ainda — inicie o worker para processar"
                            : "Nenhum resultado encontrado com os filtros atuais"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      oghRows.map((row) => (
                        <TableRow key={row.id} data-testid={`row-ogh-${row.id}`}>
                          <TableCell className="font-mono text-xs">{row.cod_imovel}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.municipio}</TableCell>
                          <TableCell className="text-center">
                            <span className="text-xs font-medium bg-muted px-1.5 py-0.5 rounded">{row.uf}</span>
                          </TableCell>
                          <TableCell className="text-right">{fmt(row.num_area, 1)}</TableCell>
                          <TableCell className="text-right text-amber-300">{fmt(row.argila_0_30, 1)}</TableCell>
                          <TableCell className="text-right text-amber-400">{fmt(row.argila_30_60, 1)}</TableCell>
                          <TableCell className="text-right text-amber-500">{fmt(row.argila_60_100, 1)}</TableCell>
                          <TableCell className="text-right font-semibold text-amber-400">{fmt(row.argila_media, 1)}</TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">{fmt(row.centroid_lat, 5)}</TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">{fmt(row.centroid_lon, 5)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {oghPageCount > 1 && (
            <div className="flex items-center justify-between" data-testid="pagination-ogh">
              <Button variant="outline" size="sm" onClick={() => setOghPage((p) => Math.max(0, p - 1))} disabled={oghPage === 0} data-testid="button-ogh-prev">
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">Página {oghPage + 1} de {oghPageCount}</span>
              <Button variant="outline" size="sm" onClick={() => setOghPage((p) => Math.min(oghPageCount - 1, p + 1))} disabled={oghPage >= oghPageCount - 1} data-testid="button-ogh-next">
                Próxima
              </Button>
            </div>
          )}

          {/* By UF stats */}
          {oghByUf.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="grid-ogh-uf">
              {oghByUf.map((s) => (
                <Card key={s.uf} className="hover:border-amber-500/40 transition-colors cursor-pointer"
                  onClick={() => { setOghUfFilter((prev) => prev === s.uf ? "_all" : s.uf); setOghPage(0); }}
                  data-testid={`card-ogh-uf-${s.uf}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-amber-400">{s.uf}</span>
                      <FlaskConical className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">{parseInt(s.total).toLocaleString("pt-BR")} imóveis</p>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Argila média:</span>
                      <span className="font-medium text-amber-400">{fmt(s.argila_avg, 1)}%</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Tab: Altitude ── */}
        <TabsContent value="altitude" className="space-y-4">
          {/* Filters */}
          <Card data-testid="card-filters">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1 min-w-[140px]">
                  <Label className="text-xs">UF</Label>
                  <Select value={selectedUf ?? ufFilter} onValueChange={(v) => { setSelectedUf(null); setUfFilter(v); setPage(0); }}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-trigger-uf">
                      <SelectValue placeholder="Todos os estados" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">Todos os estados</SelectItem>
                      {byUf.map((s) => (
                        <SelectItem key={s.uf} value={s.uf} data-testid={`option-uf-${s.uf}`}>
                          {s.uf} ({parseInt(s.total).toLocaleString("pt-BR")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Alt. Mín (m)</Label>
                  <Input className="h-8 text-sm w-28" type="number" placeholder="0" value={altMin}
                    onChange={(e) => { setAltMin(e.target.value); setPage(0); }} data-testid="input-alt-min" />
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Alt. Máx (m)</Label>
                  <Input className="h-8 text-sm w-28" type="number" placeholder="9999" value={altMax}
                    onChange={(e) => { setAltMax(e.target.value); setPage(0); }} data-testid="input-alt-max" />
                </div>
                {(selectedUf || ufFilter !== "_all" || altMin || altMax) && (
                  <Button variant="outline" size="sm" className="h-8"
                    onClick={() => { setUfFilter("_all"); setAltMin(""); setAltMax(""); setSelectedUf(null); setPage(0); }}
                    data-testid="button-clear-filters">
                    Limpar filtros
                  </Button>
                )}
                {selectedUf && (
                  <Badge variant="outline" className="h-8 px-3 text-primary border-primary/40" data-testid="badge-selected-uf">
                    Filtrado por: {selectedUf}
                    <button className="ml-1.5 opacity-60 hover:opacity-100" onClick={() => setSelectedUf(null)}>×</button>
                  </Badge>
                )}
                <div className="ml-auto text-xs text-muted-foreground self-end pb-1" data-testid="text-total-rows">
                  {totalRows.toLocaleString("pt-BR")} registros
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="table-imoveis">
                  <TableHeader>
                    <TableRow>
                      {([["cod_imovel", "Cod.Imóvel"], ["municipio", "Município"]] as [SortField, string][]).map(([f, l]) => (
                        <TableHead key={f} className="cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort(f)} data-testid={`th-${f}`}>
                          {l}<SortIcon field={f} sort={sort} order={order} />
                        </TableHead>
                      ))}
                      <TableHead className="text-center">UF</TableHead>
                      {([
                        ["num_area", "Área (ha)"], ["alt_media", "Alt.Méd"], ["alt_min", "Alt.Mín"],
                        ["alt_max", "Alt.Máx"], ["declividade", "Decliv%"], ["perimetro_km", "Perímetro (km)"],
                        ["temp_media", "Temp (°C)"], ["precip_anual", "Precip (mm)"],
                      ] as [SortField, string][]).map(([f, l]) => (
                        <TableHead key={f} className="cursor-pointer select-none whitespace-nowrap text-right" onClick={() => handleSort(f)} data-testid={`th-${f}`}>
                          {l}<SortIcon field={f} sort={sort} order={order} />
                        </TableHead>
                      ))}
                      <TableHead className="text-right whitespace-nowrap">Lat</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Lon</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dataLoading ? (
                      Array.from({ length: 10 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 14 }).map((_, j) => (
                            <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={14} className="text-center text-muted-foreground py-12">
                          Nenhum imóvel processado encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id} data-testid={`row-imovel-${row.id}`}>
                          <TableCell className="font-mono text-xs">{row.cod_imovel}</TableCell>
                          <TableCell className="whitespace-nowrap">{row.municipio}</TableCell>
                          <TableCell className="text-center">
                            <span className="text-xs font-medium bg-muted px-1.5 py-0.5 rounded">{row.uf}</span>
                          </TableCell>
                          <TableCell className="text-right">{fmt(row.num_area, 1)}</TableCell>
                          <TableCell className="text-right font-medium">{fmt(row.alt_media, 0)}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{fmt(row.alt_min, 0)}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{fmt(row.alt_max, 0)}</TableCell>
                          <TableCell className="text-right">{fmt(row.declividade, 1)}</TableCell>
                          <TableCell className="text-right">{fmt(row.perimetro_km, 1)}</TableCell>
                          <TableCell className="text-right">{fmt(row.temp_media, 1)}</TableCell>
                          <TableCell className="text-right">{fmt(row.precip_anual, 0)}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{fmt(row.centroid_lat, 5)}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{fmt(row.centroid_lon, 5)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {pageCount > 1 && (
            <div className="flex items-center justify-between" data-testid="pagination-imoveis">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="button-prev-page">
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">Página {page + 1} de {pageCount}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1} data-testid="button-next-page">
                Próxima
              </Button>
            </div>
          )}

          {/* By UF cards */}
          {byUf.length > 0 && (
            <>
              <p className="text-sm text-muted-foreground">Clique em um estado para filtrar a tabela</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="grid-uf-cards">
                {byUf.map((s) => {
                  const isActive = selectedUf === s.uf;
                  return (
                    <Card key={s.uf}
                      className={`cursor-pointer transition-all hover:border-primary/60 ${isActive ? "border-primary ring-1 ring-primary" : ""}`}
                      onClick={() => handleUfCardClick(s.uf)}
                      data-testid={`card-uf-${s.uf}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold text-primary">{s.uf}</span>
                          {isActive && <Badge variant="outline" className="text-primary border-primary text-xs">Selecionado</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">{parseInt(s.total).toLocaleString("pt-BR")} imóveis</p>
                        <div className="space-y-1.5 text-xs">
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Mountain className="h-3 w-3 shrink-0" />
                            <span>Alt média:</span>
                            <span className="ml-auto font-medium text-foreground">{fmt(s.alt_media_avg, 0)} m</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Thermometer className="h-3 w-3 shrink-0" />
                            <span>Temp.:</span>
                            <span className="ml-auto font-medium text-foreground">{fmt(s.temp_avg, 1)} °C</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Droplets className="h-3 w-3 shrink-0" />
                            <span>Precip.:</span>
                            <span className="ml-auto font-medium text-foreground">{fmt(s.precip_avg, 0)} mm</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span>Perim.:</span>
                            <span className="ml-auto font-medium text-foreground">{fmt(s.perim_avg, 1)} km</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
